import { UseFormReturn, useForm, useNavigate } from '@fukuri-admins/hooks/common';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { zodResolver } from '@hookform/resolvers/zod';
import { useGetPartnerAdminsGroups } from '~/hooks';
import { getTopBannersLink } from '~/utils/getLinks';
import { generateRequest } from '../logics/generateRequest';
import { CreateTopBannerFormFieldValues, createTopBannerSchema } from '../logics/schema';
import { CreateTopBannerFormPresentation } from '../presentations';
import { useGetPartnerAdminsTopBanners } from './useGetPartnerAdminsTopBanners';
import { usePostPartnerAdminsTopBanners } from './usePostPartnerAdminsTopBanners';

export { useGetPartnerAdminsTopBanners } from './useGetPartnerAdminsTopBanners';
export { usePostPartnerAdminsTopBanners } from './usePostPartnerAdminsTopBanners';

/**
 * トップバナー新規作成時の sort_order 算出用の取得件数
 * API 既定の 25 件では 26 件目以降が最大値算出の対象から漏れ、既存バナーと sort_order が重複するため 50 件を明示指定する（API 上限は 100）
 * NOTE: 51 件以上への対応は PEN-1118 の残スコープとして別チケットで対応する
 */
const TOP_BANNERS_FETCH_LIMIT = 50;

type PresentationProps = React.ComponentProps<typeof CreateTopBannerFormPresentation>;

type IUseCreateTopBannerForm = () => {
  state: {
    methods: UseFormReturn<CreateTopBannerFormFieldValues>;
    isButtonDisabled: boolean;
    groupsData: ReturnType<typeof useGetPartnerAdminsGroups>['data'];
  };
  actions: {
    handleOnSubmit: PresentationProps['onSubmit'];
  };
};

/**
 * トップバナー作成フォームのカスタムフック
 *
 * @returns state.methods - react-hook-formのメソッド
 * @returns state.isMutating - API実行中フラグ
 * @returns actions.handleOnSubmit - フォーム送信ハンドラ
 */
export const useCreateTopBannerForm: IUseCreateTopBannerForm = () => {
  const navigate = useNavigate();
  const threeLetterCode = getThreeLetterCode();
  const { showToast } = useToast();

  // 既存のトップバナー一覧を取得（sortOrder計算用）
  const { data: topBannersData } = useGetPartnerAdminsTopBanners({
    threeLetterCode,
    limit: TOP_BANNERS_FETCH_LIMIT,
  });

  // グループ一覧を取得
  const { data: groupsData } = useGetPartnerAdminsGroups({ threeLetterCode });

  const methods = useForm<CreateTopBannerFormFieldValues>({
    defaultValues: {
      title: '',
      transitionUrl: '',
      restrictedToApp: 'false',
      status: '0',
      image: undefined,
      groupId: undefined,
      targetUserType: 'everyone',
    },
    criteriaMode: 'all',
    mode: 'onTouched',
    resolver: zodResolver(createTopBannerSchema),
  });

  const { trigger, isMutating } = usePostPartnerAdminsTopBanners(threeLetterCode, {
    onSuccess: ({ top_banner: { title } }) => {
      showToast({
        message: `「${title}」を作成しました。`,
        status: 'success',
      });
      navigate(getTopBannersLink());
    },
    onError: error => {
      if (error.response?.status === 422) {
        const errors = error.response?.data?.errors;
        showToast({
          message: errors?.[0] ?? 'バナーの作成に失敗しました。',
          status: 'danger',
        });
        return;
      }

      showToast({
        message: 'バナーの作成に失敗しました。',
        status: 'danger',
      });
    },
  });

  const handleOnSubmit = methods.handleSubmit(async formValues => {
    const requestBody = await generateRequest({
      formValues,
      topBanners: topBannersData.top_banners,
    });
    await trigger(requestBody);
  });

  const isButtonDisabled =
    methods.formState.isSubmitting || !methods.formState.isValid || isMutating;

  return {
    state: { methods, isButtonDisabled, groupsData },
    actions: { handleOnSubmit },
  };
};
